import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import TipOffSection from "@/components/feature/TipOffSection";
import { useSeo } from "@/hooks/useSeo";
import { principles, siteInfo, stats, testimonials, workflow } from "@/mocks/site";

const ABOUT_TITLE = "关于我们｜瓜田档案的编辑原则与证据链整理流程";
const ABOUT_DESC =
  "瓜田档案是一个以独家黑料实锤与证据链整理为核心的内容站。这里公开我们的网站定位、内容原则、编辑流程与联系方式，也说明我们如何看待独家黑料截图、录音与细节的核对。";
const ABOUT_KEYWORDS = "独家黑料出处,独家黑料细节,瓜田档案,证据链整理,内容原则";

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: ABOUT_TITLE,
      description: ABOUT_DESC,
      inLanguage: "zh-CN",
    },
    {
      "@type": "Organization",
      name: siteInfo.name,
      description: siteInfo.intro,
      email: siteInfo.email,
      foundingDate: "2024-09",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://yijiangshui2023.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们" },
      ],
    },
  ],
};

export default function About() {
  useSeo({
    title: ABOUT_TITLE,
    description: ABOUT_DESC,
    keywords: ABOUT_KEYWORDS,
    path: "/about",
    jsonLd: aboutJsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="px-4 md:px-8 py-8 md:py-14">
            <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  一个把吃瓜当作「材料整理」来做的地方
                </h1>
                <p className="mt-5 text-sm md:text-base leading-8 text-foreground-600">
                  {siteInfo.intro}
                  我们不追速度，只在意一条信息能不能回到它的出处；我们也不替任何人下判决，只把能相互印证的部分写成结论。
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/archive"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-folder-open-line" />
                    浏览档案库
                  </Link>
                  <a
                    href="#contact"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 whitespace-nowrap hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-customer-service-2-line" />
                    联系我们
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden border border-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20scene%20of%20a%20warm%20archive%20desk%20with%20stacked%20folders%2C%20a%20brass%20lamp%20and%20a%20red%20stamp%2C%20aged%20paper%20texture%2C%20crimson%20and%20gold%20palette%2C%20soft%20window%20light%2C%20no%20faces&width=1200&height=900&seq=gt-about-room&orientation=landscape"
                    alt="瓜田档案编辑部整理独家黑料证据链的工作台"
                    title="瓜田档案编辑部工作台 - 证据链整理"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {stats.map((item) => (
              <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{item.value}</p>
                <p className="mt-1.5 text-sm font-medium text-foreground-700">{item.label}</p>
                <p className="mt-1 text-xs text-foreground-500">{item.note}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full bg-background-100 border-y border-background-200">
          <div className="px-4 md:px-8 py-14 md:py-20">
            <SectionHeading
              eyebrow="Principles"
              title="我们怎么看待一条独家黑料"
              desc="爆料的价值不在于够不够刺激，而在于它能不能被核对。下面四条，是我们判断一条信息是否值得写成档案的标准。"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {principles.map((item, index) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
                >
                  <span className="flex w-12 h-12 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <div>
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                      <span className="mr-2 text-sm font-normal text-foreground-400">0{index + 1}</span>
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="Workflow"
            title="从线索到档案的四步"
            desc="流程公开，是为了让读者知道每条档案是怎么来的，也方便你判断我们可能在哪里出错。"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {workflow.map((step, index) => (
              <div key={step.step} className="relative rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="font-heading text-3xl font-bold text-background-400">{step.step}</span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
                {index < workflow.length - 1 && (
                  <i className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center text-foreground-300 ri-arrow-right-line" />
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="w-full bg-secondary-950 text-background-100">
          <div className="px-4 md:px-8 py-14 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4">
              <p className="text-xs tracking-widest text-secondary-300 uppercase">Our Stance</p>
              <h2 className="mt-3 font-heading text-xl md:text-3xl font-bold">
                理性吃瓜，是让证据先说话
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p className="text-sm md:text-base leading-8 text-background-100/80">
                我们见过太多「一句话定罪」的场面：一张没出处的截图、一段被剪过的录音，就足以让一个人被讨论整周。
                瓜田档案想做的恰恰相反——把时间线摆开，把出处标上，把不确定的地方写成不确定。
                看得慢一点，但看得清楚一点。
              </p>
              <p className="mt-5 text-sm text-background-100/60">—— {siteInfo.fullName} · 编辑部</p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="Readers"
            title="读者怎么评价"
            desc="这些评价来自长期使用档案的读者，我们原样保留，也包括批评的部分。"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {testimonials.map((item, index) => (
              <figure key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <div className="flex items-center gap-3">
                  <span
                    className={`flex w-11 h-11 items-center justify-center rounded-md font-heading text-lg font-bold ${
                      index % 3 === 0
                        ? "bg-primary-100 text-primary-700"
                        : index % 3 === 1
                          ? "bg-accent-100 text-accent-900"
                          : "bg-secondary-100 text-secondary-800"
                    }`}
                  >
                    {item.name.slice(0, 1)}
                  </span>
                  <figcaption>
                    <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </figcaption>
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">“{item.text}”</blockquote>
              </figure>
            ))}
          </div>
        </section>

        <div id="contact" className="scroll-mt-24">
          <TipOffSection id="contact-form" />
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}